import { useState, useEffect } from "react";
import { PREMIUM_POPUP_CONFIGS, getEditablePopupConfig, saveEditablePopupConfig } from "@/components/feature/PremiumLockedButton";
import type { PremiumPopupConfig, PremiumPopupAdvantage } from "@/components/feature/PremiumLockedButton";
import { siteData, ColorPalette, defaultPalette } from "@/store/siteData";
import { apiGetUsers, apiUpdateUser } from "@/utils/apiClient";

// Geliştirici panel giriş şifresi ve TOTP secret
const DEV_SESSION_KEY  = "yg_dev_auth";
const DEV_PASS_KEY     = "yg_dev_password";
const DEV_TOTP_KEY     = "yg_dev_totp_secret";
const DEFAULT_DEV_PASS = "1-*vcdsAa";

// Admin (panel) şifresi
const ADMIN_PASS_KEY   = "yg_admin_password";
const DEFAULT_ADMIN_PASS = "admin123";

function getDevPassword(): string {
  return localStorage.getItem(DEV_PASS_KEY) || DEFAULT_DEV_PASS;
}
function getTotpSecret(): string {
  return localStorage.getItem(DEV_TOTP_KEY) || "";
}
function getAdminPassword(): string {
  return localStorage.getItem(ADMIN_PASS_KEY) || DEFAULT_ADMIN_PASS;
}

// ── TOTP (Google Authenticator compatible) ─────────────────────────────────

/** Base32 decode */
function base32Decode(encoded: string): Uint8Array {
  const base32Chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  const clean = encoded.toUpperCase().replace(/=+$/, "");
  let bits = 0, value = 0;
  const output: number[] = [];
  for (const char of clean) {
    const idx = base32Chars.indexOf(char);
    if (idx < 0) continue;
    value = (value << 5) | idx;
    bits += 5;
    if (bits >= 8) { bits -= 8; output.push((value >> bits) & 0xff); }
  }
  return new Uint8Array(output);
}

/** Generate random Base32 secret */
function generateTotpSecret(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
  let result = "";
  const arr = new Uint8Array(20);
  crypto.getRandomValues(arr);
  arr.forEach(b => { result += chars[b % 32]; });
  return result;
}

/** Compute TOTP code (RFC 6238) */
async function computeTotp(secret: string, timeStep = 30): Promise<string> {
  const key = base32Decode(secret);
  const counter = Math.floor(Date.now() / 1000 / timeStep);
  const counterBytes = new Uint8Array(8);
  let tmp = counter;
  for (let i = 7; i >= 0; i--) { counterBytes[i] = tmp & 0xff; tmp = Math.floor(tmp / 256); }
  const cryptoKey = await crypto.subtle.importKey("raw", key, { name: "HMAC", hash: "SHA-1" }, false, ["sign"]);
  const sig = await crypto.subtle.sign("HMAC", cryptoKey, counterBytes);
  const hmac = new Uint8Array(sig);
  const offset = hmac[19] & 0x0f;
  const code = ((hmac[offset] & 0x7f) << 24) | ((hmac[offset+1] & 0xff) << 16) | ((hmac[offset+2] & 0xff) << 8) | (hmac[offset+3] & 0xff);
  return String(code % 1000000).padStart(6, "0");
}

/** Verify TOTP (±1 window) */
async function verifyTotp(secret: string, token: string): Promise<boolean> {
  const timeStep = 30;
  const t = Math.floor(Date.now() / 1000 / timeStep);
  for (const delta of [-1, 0, 1]) {
    const counter = t + delta;
    const counterBytes = new Uint8Array(8);
    let tmp = counter;
    for (let i = 7; i >= 0; i--) { counterBytes[i] = tmp & 0xff; tmp = Math.floor(tmp / 256); }
    const key = base32Decode(secret);
    const cryptoKey = await crypto.subtle.importKey("raw", key, { name: "HMAC", hash: "SHA-1" }, false, ["sign"]);
    const sig = await crypto.subtle.sign("HMAC", cryptoKey, counterBytes);
    const hmac = new Uint8Array(sig);
    const offset = hmac[19] & 0x0f;
    const code = ((hmac[offset] & 0x7f) << 24) | ((hmac[offset+1] & 0xff) << 16) | ((hmac[offset+2] & 0xff) << 8) | (hmac[offset+3] & 0xff);
    if (String(code % 1000000).padStart(6, "0") === token) return true;
  }
  return false;
}

// ── Feature System ────────────────────────────────────────────────────────────

interface FeatureToggle {
  id: string;
  label: string;
  description: string;
  category: string;
  icon: string;
  defaultOn: boolean;
  isPremium?: boolean;
}

export type FeatureMode = "lock" | "hide";

const FEATURES: FeatureToggle[] = [
  // İletişim
  { id: "contact_form",       label: "İletişim Formu (Ana Sayfa)",      description: "Ana sayfadaki iletişim formu bölümü",                                                    category: "İletişim",   icon: "ri-mail-send-line",          defaultOn: true },
  // Navigasyon
  { id: "navbar",             label: "Üst Navigasyon",                 description: "Sitenin tüm sayfalarındaki üst menü çubuğu",                                             category: "Navigasyon", icon: "ri-menu-line",               defaultOn: true },
  { id: "footer",             label: "Alt Bilgi (Footer)",             description: "Sayfanın en altındaki footer alanı ve içeriği",                                          category: "Navigasyon", icon: "ri-layout-bottom-line",      defaultOn: true },
  // Ana Sayfa Bölümleri
  { id: "hero_section",       label: "Hero Bölümü",                    description: "Ana sayfanın en üstündeki büyük tanıtım ve hero alanı",                                  category: "Ana Sayfa",  icon: "ri-layout-top-2-line",       defaultOn: true },
  { id: "projects_preview",   label: "Proje Ön İzleme",               description: "Ana sayfadaki öne çıkan proje kartları bölümü",                                          category: "Ana Sayfa",  icon: "ri-building-2-line",         defaultOn: true },
  { id: "testimonials",       label: "Referanslar Bölümü",             description: "Müşteri yorumları ve referans alıntıları bölümü",                                        category: "Ana Sayfa",  icon: "ri-double-quotes-l",         defaultOn: true },
  { id: "partners_section",   label: "Stratejik Ortaklar Bölümü",     description: "Ana sayfadaki stratejik ortaklar tanıtım alanı",                                         category: "Ana Sayfa",  icon: "ri-links-line",              defaultOn: true },
  { id: "cta_section",        label: "CTA Bölümü",                     description: "Ana sayfadaki randevu al / iletişim eylem çağrısı alanı",                               category: "Ana Sayfa",  icon: "ri-cursor-line",             defaultOn: true },
  { id: "features_section",   label: "Özellikler Bölümü",             description: "Şirketin teknik kapasitesini ve özelliklerini gösteren kart alanı",                      category: "Ana Sayfa",  icon: "ri-star-line",               defaultOn: true },
  // Projeler
  { id: "drag_drop_projects", label: "Projelerde Sürükle-Bırak",       description: "Proje listesinde elle sıralama — kapalıyken tarih sıralaması aktif olur",                category: "Projeler",   icon: "ri-drag-drop-line",          defaultOn: true },
];

// Premium özellikler — ayrı sekmede yönetilir
const PREMIUM_FEATURES: FeatureToggle[] = [
  { id: "livesupport",        label: "Destek Merkezi",                  description: "Kontrol paneli Destek Merkezi menüsü ve site üzerindeki canlı destek widgetı",            category: "Yönetim",    icon: "ri-headphone-line",          defaultOn: true,  isPremium: true },
  { id: "site_inspector",     label: "Görsel İnspektör",                description: "Canlı site üzerinde içerik düzenleyici araç",                                            category: "Yönetim",    icon: "ri-cursor-line",             defaultOn: true,  isPremium: true },
  { id: "changelog",          label: "Değişiklik Günlüğü",             description: "Admin panelindeki tüm değişikliklerin geçmiş kaydı",                                     category: "Yönetim",    icon: "ri-history-line",            defaultOn: true,  isPremium: true },
  { id: "ad_manager",         label: "Reklam Yönetimi",               description: "Siteye ilk girişte ziyaretçilere gösterilecek tam ekran popup reklam sistemi",           category: "Yönetim",    icon: "ri-advertisement-line",      defaultOn: false, isPremium: true },
  { id: "color_palette",      label: "Renk Paleti",                    description: "Site Ayarları içinde renk paleti sekmesi — tüm platformu renklendir",                   category: "Arayüz",    icon: "ri-palette-line",            defaultOn: true,  isPremium: true },
  { id: "undo_enabled",       label: "Sınırsız Geri Al",               description: "Açıkken sınırsız geri alma — Kapalıyken 24 saatte 3 geri alma hakkı",                    category: "Arayüz",    icon: "ri-arrow-go-back-line",      defaultOn: true,  isPremium: true },
  { id: "status_tags",        label: "Proje Durum Etiketleri",         description: "Proje durum etiketlerini özelleştirme ve yönetme ekranı",                                category: "Projeler",   icon: "ri-price-tag-3-line",        defaultOn: true,  isPremium: true },
  { id: "project_archive",    label: "Proje Arşivleme",                description: "Projeleri arşivleyip siteden gizleyebilme özelliği",                                     category: "Projeler",   icon: "ri-archive-line",            defaultOn: true,  isPremium: true },

];

// Tüm feature'lar birleşik (state mgmt için)
const ALL_FEATURES = [...FEATURES, ...PREMIUM_FEATURES];

function getFeatureStates(): Record<string, boolean> {
  const fromStore = siteData.getFeatureToggles();
  if (Object.keys(fromStore).length > 0) return fromStore;
  
  try { const raw = localStorage.getItem("yg_feature_toggles"); if (raw) return JSON.parse(raw); } catch { /**/ }
  const defaults: Record<string, boolean> = {};
  ALL_FEATURES.forEach(f => { defaults[f.id] = f.defaultOn; });
  return defaults;
}

function getFeatureModes(): Record<string, FeatureMode> {
  const fromStore = siteData.getFeatureModes() as Record<string, FeatureMode>;
  if (Object.keys(fromStore).length > 0) return fromStore;
  
  try { const raw = localStorage.getItem("yg_feature_modes"); if (raw) return JSON.parse(raw); } catch { /**/ }
  return {};
}

// ── Premium Trial ────────────────────────────────────────────────────────────

const PREMIUM_TRIAL_KEY = "yg_premium_trial";
const PREMIUM_TRIAL_HOURS_KEY = "yg_premium_trial_hours"; // toplam saat
const DEFAULT_TRIAL_HOURS = 72; // 3 gün

export function getPremiumTrialHours(): number {
  try {
    // Eski gün anahtarını okuyup saate çevir (geriye dönük uyumluluk)
    const oldRaw = localStorage.getItem("yg_premium_trial_days");
    const newRaw = localStorage.getItem(PREMIUM_TRIAL_HOURS_KEY);
    if (newRaw) {
      const h = parseInt(newRaw, 10);
      if (!isNaN(h) && h > 0) return h;
    }
    if (oldRaw) {
      const d = parseInt(oldRaw, 10);
      if (!isNaN(d) && d > 0) return d * 24;
    }
  } catch { /**/ }
  return DEFAULT_TRIAL_HOURS;
}

/** Geriye dönük uyumluluk */
export function getPremiumTrialDays(): number {
  return Math.ceil(getPremiumTrialHours() / 24);
}

export function setPremiumTrialDuration(days: number, hours: number): void {
  const total = days * 24 + hours;
  if (total > 0) localStorage.setItem(PREMIUM_TRIAL_HOURS_KEY, String(total));
}

export function clearPremiumTrial(): void {
  localStorage.removeItem(PREMIUM_TRIAL_KEY);
}

function getTrialMs(): number {
  return getPremiumTrialHours() * 60 * 60 * 1000;
}

export function isPremiumTrialActive(): boolean {
  try {
    const raw = localStorage.getItem(PREMIUM_TRIAL_KEY);
    if (!raw) return false;
    const { startedAt } = JSON.parse(raw) as { startedAt: number };
    return Date.now() - startedAt < getTrialMs();
  } catch { return false; }
}

export function getPremiumTrialInfo(): { active: boolean; startedAt: number | null; expiresAt: number | null; remainingMs: number } {
  try {
    const raw = localStorage.getItem(PREMIUM_TRIAL_KEY);
    if (!raw) return { active: false, startedAt: null, expiresAt: null, remainingMs: 0 };
    const { startedAt } = JSON.parse(raw) as { startedAt: number };
    const expiresAt = startedAt + getTrialMs();
    const remainingMs = Math.max(0, expiresAt - Date.now());
    return { active: remainingMs > 0, startedAt, expiresAt, remainingMs };
  } catch { return { active: false, startedAt: null, expiresAt: null, remainingMs: 0 }; }
}

export function activatePremiumTrial(): void {
  localStorage.setItem(PREMIUM_TRIAL_KEY, JSON.stringify({ startedAt: Date.now() }));
}

export function isFeatureEnabled(id: string): boolean {
  // Premium deneme süresi aktifse tüm özellikler açık
  if (isPremiumTrialActive()) return true;
  const states = getFeatureStates();
  if (id in states) return states[id];
  return ALL_FEATURES.find(f => f.id === id)?.defaultOn ?? true;
}

export function getFeatureMode(id: string): FeatureMode {
  return getFeatureModes()[id] ?? "hide";
}

export function getFeatureVisibility(id: string): "visible" | "locked" | "hidden" {
  if (isFeatureEnabled(id)) return "visible";
  return getFeatureMode(id) === "lock" ? "locked" : "hidden";
}

export function getFeatureDescription(id: string): string {
  return ALL_FEATURES.find(f => f.id === id)?.description ?? "";
}

// ── Color Palette ─────────────────────────────────────────────────────────────

const PRESET_PALETTES: { name: string; palette: ColorPalette }[] = [
  { name: "Gece Mavisi (Varsayılan)", palette: defaultPalette },
  { name: "Toprak Tonu",   palette: { primary:"#8B6E4E", primaryDark:"#6b5240", accent:"#A0856B", bgBase:"#0E0A08", bgPanel:"#14100C", bgCard:"#1E1812", border:"#3D3228", textMuted:"#8B7562" } },
  { name: "Orman Yeşili",  palette: { primary:"#4A7C59", primaryDark:"#365C43", accent:"#6B9E7A", bgBase:"#080E0A", bgPanel:"#0C140E", bgCard:"#131F16", border:"#1E3324", textMuted:"#6B8E76" } },
  { name: "Koyu Çelik",    palette: { primary:"#5C6E8A", primaryDark:"#445060", accent:"#8A9BB5", bgBase:"#07090D", bgPanel:"#0D1018", bgCard:"#131820", border:"#252D3E", textMuted:"#7B8899" } },
  { name: "Bordo & Krem",  palette: { primary:"#8B3A3A", primaryDark:"#6B2A2A", accent:"#C4725A", bgBase:"#0E0808", bgPanel:"#140C0C", bgCard:"#1E1212", border:"#3D2222", textMuted:"#9B7070" } },
];

export function applyPalette(p: ColorPalette) {
  const old = document.getElementById("yg-palette-style");
  if (old) old.remove();
  // Directly set body background for immediate effect
  document.body.style.backgroundColor = p.bgBase;
  document.documentElement.style.setProperty('--yg-bg-base', p.bgBase);
  const style = document.createElement("style");
  style.id = "yg-palette-style";
  style.textContent = `
    :root {
      --yg-primary: ${p.primary};
      --yg-primary-dark: ${p.primaryDark};
      --yg-accent: ${p.accent};
      --yg-bg-base: ${p.bgBase};
      --yg-bg-panel: ${p.bgPanel};
      --yg-bg-card: ${p.bgCard};
      --yg-border: ${p.border};
      --yg-text-muted: ${p.textMuted};
    }
    html, body { background-color: ${p.bgBase} !important; }
    .bg-\\[\\#050A14\\] { background-color: ${p.bgBase} !important; }
    .bg-\\[\\#0A1020\\], .bg-\\[\\#0F1729\\] { background-color: ${p.bgPanel} !important; }
    .bg-\\[\\#141E35\\], .bg-\\[\\#1E2B4A\\] { background-color: ${p.bgCard} !important; }
    .border-\\[\\#2B3654\\] { border-color: ${p.border} !important; }
    .text-\\[\\#7B8FAF\\], .text-\\[\\#5E7398\\] { color: ${p.textMuted} !important; }
    .bg-\\[\\#3B82F6\\] { background-color: ${p.primary} !important; }
    .text-\\[\\#3B82F6\\] { color: ${p.primary} !important; }
    .border-\\[\\#3B82F6\\] { border-color: ${p.primary} !important; }
    .hover\\:bg-\\[\\#3d4e6a\\]:hover { background-color: ${p.primaryDark} !important; }
  `;
  document.head.appendChild(style);
}

export function getSavedPalette(): ColorPalette {
  const p = siteData.getColorPalette();
  if (p && p.primary !== defaultPalette.primary) return p;
  
  try { const raw = localStorage.getItem("yg_color_palette"); if (raw) return JSON.parse(raw); } catch { /**/ }
  return defaultPalette;
}

export function ColorPalettePanel() {
  const [palette, setPalette] = useState<ColorPalette>(getSavedPalette);
  const [saved, setSaved] = useState(false);

  const colorFields: { key: keyof ColorPalette; label: string; desc: string }[] = [
    { key: "primary",     label: "Ana Renk",         desc: "Butonlar, aktif menü, vurgular" },
    { key: "primaryDark", label: "Ana Renk (Koyu)",  desc: "Hover durumu" },
    { key: "accent",      label: "Vurgu Rengi",       desc: "İkincil metin ve ikonlar" },
    { key: "bgBase",      label: "Sayfa Arka Planı",  desc: "En derin arka plan" },
    { key: "bgPanel",     label: "Panel Arka Planı",  desc: "Header, sidebar" },
    { key: "bgCard",      label: "Kart Arka Planı",   desc: "İçerik kutuları" },
    { key: "border",      label: "Kenarlık Rengi",    desc: "Tüm çerçeve çizgileri" },
    { key: "textMuted",   label: "Soluk Metin",       desc: "Alt başlıklar, yardımcı metin" },
  ];

  const handleSave = async () => { 
    applyPalette(palette); 
    await siteData.saveColorPalette(palette);
    setSaved(true); 
    setTimeout(() => setSaved(false), 2000); 
  };
  const applyPreset = (p: ColorPalette) => { setPalette(p); applyPalette(p); };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-pink-500/15 flex-shrink-0">
          <i className="ri-palette-line text-pink-400 text-xl"></i>
        </div>
        <div>
          <h2 className="text-white font-semibold">Site Renk Paleti</h2>
          <p className="text-[#7B8FAF] text-xs">Hazır tema seç veya renkleri tek tek özelleştir — site + kontrol paneli dahil tüm platform güncellenir</p>
        </div>
      </div>
      <div>
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-3">Hazır Temalar</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PRESET_PALETTES.map(({ name, palette: p }) => (
            <button key={name} onClick={() => applyPreset(p)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#2B3654] bg-[#0F1729] hover:border-pink-500/40 transition-all cursor-pointer text-left">
              <div className="flex gap-1 flex-shrink-0">
                {[p.primary, p.bgCard, p.border].map((c, i) => (
                  <span key={i} className="w-4 h-4 rounded-full" style={{ background: c }}></span>
                ))}
              </div>
              <span className="text-[#A5B5CC] text-sm truncate">{name}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-3">Manuel Düzenleme</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {colorFields.map(({ key, label, desc }) => (
            <div key={key} className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-3 flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-lg border-2 border-white/10 overflow-hidden">
                  <input type="color" value={palette[key]}
                    onChange={e => setPalette(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-14 h-14 -mt-1 -ml-1 cursor-pointer border-0 bg-transparent" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium">{label}</p>
                <p className="text-[#5E7398] text-xs">{desc}</p>
                <p className="text-[#465886] text-xs font-mono mt-0.5">{palette[key]}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={() => { setPalette(defaultPalette); applyPalette(defaultPalette); }}
          className="flex-1 py-2.5 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-xl hover:text-white cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
          <i className="ri-refresh-line"></i> Varsayılana Dön
        </button>
        <button onClick={handleSave}
          className={`flex-1 py-2.5 text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 ${saved ? "bg-emerald-600 text-white" : "bg-pink-600 hover:bg-pink-700 text-white"}`}>
          {saved ? <><i className="ri-check-double-line"></i> Uygulandı!</> : <><i className="ri-save-line"></i> Paleti Uygula</>}
        </button>
      </div>
    </div>
  );
}

// ── Premium Trial Panel ──────────────────────────────────────────────

function parseDaysHours(totalHours: number): { days: number; hours: number } {
  return { days: Math.floor(totalHours / 24), hours: totalHours % 24 };
}

function PremiumTrialBanner() {
  const [tick, setTick] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [deletingConfirm, setDeletingConfirm] = useState(false);

  const totalHoursInit = getPremiumTrialHours();
  const parsed = parseDaysHours(totalHoursInit);
  const [daysInput, setDaysInput] = useState<number>(parsed.days);
  const [hoursInput, setHoursInput] = useState<number>(parsed.hours);
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const totalInputHours = daysInput * 24 + hoursInput;

  const handleSaveDuration = () => {
    if (totalInputHours > 0) {
      setPremiumTrialDuration(daysInput, hoursInput);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const info = getPremiumTrialInfo();

  const formatRemaining = (ms: number) => {
    const totalMins = Math.floor(ms / 60000);
    const d = Math.floor(totalMins / (60 * 24));
    const h = Math.floor((totalMins % (60 * 24)) / 60);
    const m = totalMins % 60;
    if (d > 0 && h > 0) return `${d}g ${h}sa`;
    if (d > 0) return `${d} gün`;
    if (h > 0) return `${h}sa ${m}dk`;
    return `${m}dk`;
  };

  const formatDuration = () => {
    const current = parseDaysHours(getPremiumTrialHours());
    const parts: string[] = [];
    if (current.days > 0) parts.push(`${current.days} gün`);
    if (current.hours > 0) parts.push(`${current.hours} saat`);
    return parts.join(" ") || "0 saat";
  };

  const handleActivate = () => {
    if (!confirming) { setConfirming(true); return; }
    activatePremiumTrial();
    setConfirming(false);
    setTick(t => t + 1);
  };

  const handleDelete = () => {
    if (!deletingConfirm) { setDeletingConfirm(true); return; }
    clearPremiumTrial();
    setDeletingConfirm(false);
    setTick(t => t + 1);
  };

  if (info.active) {
    return (
      <div key={tick} className="bg-amber-500/10 border border-amber-500/25 rounded-2xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-500/20 flex-shrink-0">
            <i className="ri-vip-crown-fill text-amber-400 text-2xl"></i>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-amber-300 font-bold text-base">Premium Deneme Aktif</h3>
              <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold border border-amber-500/30">
                Tüm Özellikler Açık
              </span>
            </div>
            <p className="text-amber-400/80 text-sm mt-1.5">
              Tüm premium özellikler aktif. Süre dolunca bireysel toggle ayarlarına geri döner.
            </p>
            <div className="flex items-center gap-3 mt-3">
              <div className="flex items-center gap-2 bg-black/30 px-3 py-1.5 rounded-lg">
                <i className="ri-timer-line text-amber-400 text-sm"></i>
                <span className="text-amber-300 text-sm font-semibold">{formatRemaining(info.remainingMs)} kaldı</span>
              </div>
              <div className="flex-1 h-2 bg-black/30 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-1000"
                  style={{ width: `${(info.remainingMs / getTrialMs()) * 100}%` }}
                />
              </div>
            </div>

            {/* Sil butonu */}
            <div className="mt-4 flex items-center gap-3">
              {deletingConfirm ? (
                <>
                  <p className="text-red-400 text-xs">Premium süresini sonlandırmak istediğine emin misin?</p>
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-semibold rounded-lg border border-red-500/30 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-delete-bin-line"></i> Evet, Sonlandır
                  </button>
                  <button
                    onClick={() => setDeletingConfirm(false)}
                    className="px-3 py-1.5 text-[#7B8FAF] text-xs border border-[#2B3654] rounded-lg hover:text-white cursor-pointer whitespace-nowrap transition-colors"
                  >
                    İptal
                  </button>
                </>
              ) : (
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-lg border border-red-500/20 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-delete-bin-line"></i> Denemeyi Sonlandır
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-5">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#3B82F6]/20 flex-shrink-0">
          <i className="ri-vip-crown-line text-[#7B8FAF] text-2xl"></i>
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-base">Premium Deneme Süresi</h3>
          <p className="text-[#7B8FAF] text-sm mt-1">
            Ücretsiz deneme ile tüm premium özellikleri keşfet. Süre dolunca mevcut ayarlara döner.
          </p>

          {/* Gün + Saat süre ayarı */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#7B8FAF] text-xs uppercase tracking-widest">Deneme Süresi</span>
              {!editing ? (
                <button
                  onClick={() => {
                    const c = parseDaysHours(getPremiumTrialHours());
                    setDaysInput(c.days); setHoursInput(c.hours); setEditing(true);
                  }}
                  className="flex items-center gap-1 text-[#3B82F6] hover:text-white text-xs cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-pencil-line"></i> Düzenle
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveDuration}
                    disabled={totalInputHours === 0}
                    className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600/20 hover:bg-emerald-600/30 text-white text-xs rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors"
                  >
                    {saved ? <><i className="ri-check-double-line"></i> Kaydedildi</> : <><i className="ri-save-line"></i> Kaydet</>}
                  </button>
                  <button
                    onClick={() => { setEditing(false); setSaved(false); }}
                    className="text-[#7B8FAF] hover:text-white text-xs cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}
            </div>

            {editing ? (
              <div className="flex items-center gap-3 bg-[#141E35] border border-[#2B3654] rounded-xl px-4 py-3">
                <i className="ri-timer-2-line text-amber-400 text-lg flex-shrink-0"></i>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number" min={0} max={365} value={daysInput}
                      onChange={e => setDaysInput(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-16 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-2 py-1.5 outline-none focus:border-amber-500 text-center"
                    />
                    <span className="text-[#7B8FAF] text-sm">gün</span>
                  </div>
                  <span className="text-[#465886] text-sm">+</span>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number" min={0} max={23} value={hoursInput}
                      onChange={e => setHoursInput(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="w-16 bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-2 py-1.5 outline-none focus:border-amber-500 text-center"
                    />
                    <span className="text-[#7B8FAF] text-sm">saat</span>
                  </div>
                </div>
                {totalInputHours > 0 && (
                  <span className="text-amber-400/70 text-xs whitespace-nowrap ml-auto">
                    = {totalInputHours}s
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-[#141E35] border border-[#2B3654] rounded-xl px-4 py-3">
                <i className="ri-timer-2-line text-amber-400 text-lg flex-shrink-0"></i>
                <div>
                  <span className="text-amber-300 font-bold text-base">{formatDuration()}</span>
                  <span className="text-[#465886] text-xs ml-2">({getPremiumTrialHours()} saat toplam)</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            {["Tüm özellikler aktif", "Otomatik kapanır", "Ayarlanabilir süre"].map(f => (
              <span key={f} className="inline-flex items-center gap-1 text-xs text-emerald-400">
                <i className="ri-check-line"></i> {f}
              </span>
            ))}
          </div>

          <div className="flex gap-3 mt-4">
            {confirming ? (
              <>
                <button
                  onClick={handleActivate}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-vip-crown-fill"></i> Evet, Aktif Et
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  className="px-4 py-2 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-xl hover:text-white cursor-pointer whitespace-nowrap transition-colors"
                >
                  İptal
                </button>
              </>
            ) : (
              <button
                onClick={handleActivate}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#3B82F6] hover:bg-[#3d4e6a] text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-vip-crown-line"></i> {formatDuration()} Premium Deneme Başlat
              </button>
            )}
          </div>
          {confirming && (
            <p className="text-amber-400 text-xs mt-2">
              {formatDuration()} sürelik premium deneme başlamak üzere. Emin misin?
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── TOTP Setup Component ───────────────────────────────────────────────────────

function TotpSetupPanel({ onDone }: { onDone: () => void }) {
  const [secret] = useState(() => generateTotpSecret());
  const [verifyCode, setVerifyCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const appName = "YenigulyapiDevPanel";
  const otpauthUrl = `otpauth://totp/${appName}?secret=${secret}&issuer=Yenigulyapi`;

  const handleVerify = async () => {
    const ok = await verifyTotp(secret, verifyCode.trim());
    if (ok) {
      localStorage.setItem(DEV_TOTP_KEY, secret);
      setSuccess(true);
      setTimeout(onDone, 1500);
    } else {
      setError("Kod yanlış. Google Authenticator uygulamasından kodu tekrar girin.");
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3 flex items-start gap-3">
        <i className="ri-shield-keyhole-line text-amber-400 text-lg flex-shrink-0"></i>
        <div>
          <p className="text-amber-300 text-sm font-semibold">İki Faktörlü Doğrulama Kurulumu</p>
          <p className="text-amber-400/80 text-xs mt-0.5">Google Authenticator uygulamasını açın ve bu kodu tarayın.</p>
        </div>
      </div>

      <div className="bg-[#0F1729] border border-[#2B3654] rounded-xl p-5 space-y-4">
        <div>
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Adım 1 — Manuel Giriş Kodu</p>
          <div className="bg-[#141E35] border border-[#2B3654] rounded-lg px-4 py-3 font-mono text-white text-sm tracking-widest text-center select-all">
            {secret.match(/.{1,4}/g)?.join(" ")}
          </div>
          <p className="text-[#465886] text-xs mt-1.5 text-center">Bunu güvenli bir yere not alın!</p>
        </div>

        <div>
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Adım 2 — Uygulamadan Kod Gir</p>
          <div className="flex gap-2">
            <input
              type="text"
              maxLength={6}
              value={verifyCode}
              onChange={e => { setVerifyCode(e.target.value.replace(/\D/g, "")); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleVerify()}
              placeholder="123456"
              className="flex-1 bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500 text-center font-mono tracking-widest transition-colors"
            />
            <button onClick={handleVerify} disabled={verifyCode.length !== 6}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50">
              {success ? <i className="ri-check-double-line"></i> : "Doğrula"}
            </button>
          </div>
          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
          {success && <p className="text-emerald-400 text-xs mt-2">✓ Google Authenticator başarıyla bağlandı!</p>}
        </div>
      </div>

      <button onClick={onDone}
        className="w-full py-2 text-[#7B8FAF] text-sm border border-[#2B3654] rounded-xl hover:text-white cursor-pointer transition-colors">
        Daha Sonra Kur
      </button>
    </div>
  );
}

// ── Password Change Panel ─────────────────────────────────────────────────────

function PasswordChangePanel() {
  const [tab, setTab] = useState<"dev" | "admin">("dev");
  const [form, setForm] = useState({ current: "", newPass: "", confirm: "" });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const totpEnabled = !!getTotpSecret();

  const handleChange = async () => {
    setErr("");
    // Admin şifresini değiştirirken mevcut şifre gerekmez (geliştirici yetkisiyle)
    if (tab === "dev") {
      const currentCorrect = getDevPassword();
      if (form.current !== currentCorrect) { setErr("Mevcut şifre yanlış."); return; }
    }
    if (form.newPass.length < 6) { setErr("Yeni şifre en az 6 karakter olmalı."); return; }
    if (form.newPass !== form.confirm) { setErr("Şifreler eşleşmiyor."); return; }
    
    if (tab === "dev") {
      localStorage.setItem(DEV_PASS_KEY, form.newPass);
    } else {
      try {
        const users = await apiGetUsers();
        const adminUser = users.find(u => u.role === "admin");
        if (adminUser) {
          try {
            await apiUpdateUser(adminUser.id, { password: form.newPass });
          } catch (apiErr: any) {
            setErr("Veritabanı hatası: " + (apiErr.message || "Bilinmeyen hata"));
            return;
          }
        }
      } catch (e: any) {
        setErr("Kullanıcı listesi alınamadı: " + (e.message || "Bilinmeyen hata"));
        return;
      }
      localStorage.setItem(ADMIN_PASS_KEY, form.newPass);
    }
    
    setOk(true);
    setForm({ current: "", newPass: "", confirm: "" });
    setTimeout(() => setOk(false), 2500);
  };

  if (showSetup2FA) {
    return (
      <div className="space-y-4">
        <button onClick={() => setShowSetup2FA(false)} className="flex items-center gap-1.5 text-[#7B8FAF] hover:text-white text-sm cursor-pointer transition-colors">
          <i className="ri-arrow-left-line"></i> Geri
        </button>
        <TotpSetupPanel onDone={() => setShowSetup2FA(false)} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex gap-1 bg-[#0F1729] border border-[#2B3654] rounded-xl p-1.5">
        {([["dev", "Geliştirici Şifresi"], ["admin", "Admin Şifresi"]] as const).map(([t, l]) => (
          <button key={t} onClick={() => { setTab(t); setErr(""); setOk(false); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${tab === t ? "bg-[#3B82F6] text-white" : "text-[#7B8FAF] hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {tab === "admin" && (
          <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-lg px-3 py-2 flex items-center gap-2">
            <i className="ri-shield-check-line text-[#3B82F6] text-sm"></i>
            <p className="text-[#A5B5CC] text-xs">Geliştirici yetkisiyle — mevcut şifre gerekmez</p>
          </div>
        )}
        {[
          ...(tab === "dev" ? [{ label: "Mevcut Şifre", key: "current" as const }] : []),
          { label: "Yeni Şifre", key: "newPass" as const },
          { label: "Yeni Şifre (Tekrar)", key: "confirm" as const },
        ].map(field => (
          <div key={field.key}>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{field.label}</label>
            <input
              type="password"
              value={form[field.key]}
              onChange={e => { setForm(p => ({ ...p, [field.key]: e.target.value })); setErr(""); }}
              className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors"
            />
          </div>
        ))}
        {err && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/25 text-red-400 text-xs px-3 py-2.5 rounded-lg">
            <i className="ri-error-warning-line flex-shrink-0"></i> {err}
          </div>
        )}
        {ok && (
          <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-xs px-3 py-2.5 rounded-lg">
            <i className="ri-check-double-line flex-shrink-0"></i> Şifre başarıyla değiştirildi!
          </div>
        )}
        <button onClick={handleChange}
          className="w-full py-2.5 bg-[#3B82F6] hover:bg-[#3d4e6a] text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
          <i className="ri-lock-password-line"></i> Şifreyi Değiştir
        </button>
      </div>

      {/* Google Authenticator */}
      {tab === "dev" && (
        <div className="border-t border-[#2B3654] pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-500/15">
                <i className="ri-shield-keyhole-line text-emerald-400 text-base"></i>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Google Authenticator</p>
                <p className="text-[#5E7398] text-xs">{totpEnabled ? "Aktif — her girişte kod gerekli" : "Henüz kurulmadı"}</p>
              </div>
            </div>
            <button onClick={() => setShowSetup2FA(true)}
              className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer whitespace-nowrap border transition-colors ${
                totpEnabled
                  ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
              }`}>
              {totpEnabled ? "Yeniden Kur" : "Kur"}
            </button>
          </div>
          {totpEnabled && (
            <div className="mt-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
              <p className="text-emerald-400/80 text-xs">2FA aktif. Geliştirici paneline giriş için şifre + Authenticator kodu gerekli.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ana Panel ─────────────────────────────────────────────────────────────────

type DevTab = "features" | "premium" | "colors" | "security";

export default function DevPanel() {
  const [authed, setAuthed]       = useState(() => {
    try {
      const raw = localStorage.getItem(DEV_SESSION_KEY);
      return raw ? JSON.parse(raw).authed === true : false;
    } catch { return false; }
  });
  const [password, setPassword]   = useState("");
  const [totpCode, setTotpCode]   = useState("");
  const [needTotp, setNeedTotp]   = useState(false);
  const [error, setError]         = useState("");
  const [features, setFeatures]   = useState<Record<string, boolean>>(getFeatureStates);
  const [modes, setModes]         = useState<Record<string, FeatureMode>>(getFeatureModes);
  const [saved, setSaved]         = useState(false);
  const [showPass, setShowPass]   = useState(false);
  const [activeCategory, setActiveCategory] = useState("Tümü");
  const [devTab, setDevTab]       = useState<DevTab>("features");
  const [popupEditorFeature, setPopupEditorFeature] = useState<string | null>(null);

  useEffect(() => {
    applyPalette(getSavedPalette());
  }, []);

  const categories = ["Tümü", ...Array.from(new Set(FEATURES.map(f => f.category)))];
  const premiumCategories = ["Tümü", ...Array.from(new Set(PREMIUM_FEATURES.map(f => f.category)))];
  const [activePremiumCategory, setActivePremiumCategory] = useState("Tümü");

  const handleLogin = async () => {
    if (password !== getDevPassword()) {
      setError("Yanlış şifre."); setPassword(""); return;
    }
    const totpSecret = getTotpSecret();
    if (totpSecret) {
      // TOTP gerekli
      if (!needTotp) { setNeedTotp(true); setError(""); return; }
      const ok = await verifyTotp(totpSecret, totpCode.trim());
      if (!ok) { setError("Google Authenticator kodu yanlış."); setTotpCode(""); return; }
    }
    localStorage.setItem(DEV_SESSION_KEY, JSON.stringify({ authed: true, password: password }));
    setAuthed(true); setError("");
  };

  const handleToggle = (id: string) => {
    setFeatures(prev => ({ ...prev, [id]: !prev[id] }));
    setSaved(false);
  };

  const handleModeChange = (id: string, mode: FeatureMode) => {
    setModes(prev => ({ ...prev, [id]: mode }));
    setSaved(false);
  };

  const handleSave = async () => {
    await siteData.saveFeatureToggles(features);
    await siteData.saveFeatureModes(modes);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    const defaults: Record<string, boolean> = {};
    ALL_FEATURES.forEach(f => { defaults[f.id] = f.defaultOn; });
    setFeatures(defaults); setModes({}); setSaved(false);
  };

  const filteredFeatures = activeCategory === "Tümü"
    ? FEATURES
    : FEATURES.filter(f => f.category === activeCategory);

  const filteredPremiumFeatures = activePremiumCategory === "Tümü"
    ? PREMIUM_FEATURES
    : PREMIUM_FEATURES.filter(f => f.category === activePremiumCategory);

  const enabledCount = ALL_FEATURES.filter(f => features[f.id] ?? f.defaultOn).length;

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050A14] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 flex items-center justify-center bg-[#1A2744] border border-[#2B3654] rounded-2xl mx-auto mb-5">
              <i className="ri-code-s-slash-line text-3xl text-[#3B82F6]"></i>
            </div>
            <h1 className="text-white text-xl font-bold tracking-tight">Geliştirici Paneli</h1>
            <p className="text-[#5E7398] text-sm mt-2">Yetkili giriş gereklidir</p>
          </div>

          <div className={`bg-[#0F1729] border rounded-2xl p-8 space-y-4 ${error ? "border-red-500/40" : "border-[#2B3654]"}`}>
            {!needTotp ? (
              <>
                <div>
                  <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Erişim Şifresi</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-[#3B82F6]">
                      <i className="ri-lock-line text-sm"></i>
                    </div>
                    <input type={showPass ? "text" : "password"} value={password}
                      onChange={e => { setPassword(e.target.value); setError(""); }}
                      onKeyDown={e => e.key === "Enter" && handleLogin()}
                      placeholder="••••••••••"
                      className={`w-full bg-[#141E35] border text-white text-sm rounded-lg pl-9 pr-10 py-3 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398] ${error ? "border-red-500/60" : "border-[#2B3654]"}`}
                    />
                    <button type="button" onClick={() => setShowPass(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3B82F6] cursor-pointer w-4 h-4 flex items-center justify-center">
                      <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2.5">
                  <i className="ri-shield-keyhole-line text-emerald-400 text-sm flex-shrink-0"></i>
                  <p className="text-emerald-300 text-xs">Şifre doğru! Şimdi Google Authenticator kodunu girin.</p>
                </div>
                <div>
                  <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Authenticator Kodu</label>
                  <input type="text" maxLength={6} value={totpCode}
                    onChange={e => { setTotpCode(e.target.value.replace(/\D/g, "")); setError(""); }}
                    onKeyDown={e => e.key === "Enter" && handleLogin()}
                    placeholder="6 haneli kod"
                    className={`w-full bg-[#141E35] border text-white text-sm rounded-lg px-4 py-3 outline-none focus:border-emerald-500 transition-colors placeholder-[#5E7398] text-center font-mono tracking-widest ${error ? "border-red-500/60" : "border-[#2B3654]"}`}
                  />
                </div>
              </>
            )}

            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-3 py-2.5 rounded-lg">
                <i className="ri-error-warning-line text-sm flex-shrink-0"></i>
                {error}
              </div>
            )}

            <button onClick={handleLogin} disabled={!password && !needTotp}
              className="w-full bg-[#3B82F6] hover:bg-[#3d4e6a] text-white font-medium py-3 rounded-lg transition-colors cursor-pointer text-sm whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50">
              <i className="ri-shield-check-line"></i>
              {needTotp ? "Kodu Doğrula" : "Giriş Yap"}
            </button>

            {needTotp && (
              <button onClick={() => { setNeedTotp(false); setTotpCode(""); setError(""); }}
                className="w-full py-2 text-[#7B8FAF] text-xs hover:text-white cursor-pointer transition-colors">
                ← Şifre ekranına dön
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050A14]">
      <header className="sticky top-0 z-20 bg-[#0A1020]/90 backdrop-blur border-b border-[#2B3654] px-6 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#3B82F6]/20 rounded-lg">
            <i className="ri-code-s-slash-line text-[#3B82F6] text-lg"></i>
          </div>
          <div>
            <h1 className="text-white font-bold text-sm">Geliştirici Paneli</h1>
            <p className="text-[#5E7398] text-xs">Yenigül Yapı — Özellik & Renk & Güvenlik</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-[#7B8FAF] bg-[#141E35] border border-[#2B3654] px-3 py-1.5 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            <span>{enabledCount}/{ALL_FEATURES.length} aktif</span>
          </div>
          <button onClick={() => { sessionStorage.removeItem(DEV_SESSION_KEY); setAuthed(false); setPassword(""); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[#7B8FAF] hover:text-red-400 text-xs border border-[#2B3654] hover:border-red-500/30 rounded-lg cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-logout-box-r-line"></i> Çıkış
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 pt-6">
        <div className="flex gap-2 bg-[#0F1729] border border-[#2B3654] rounded-xl p-1.5">
          {([
            { id: "features"  as DevTab, label: "Site Özellikleri",     icon: "ri-toggle-line" },
            { id: "premium"   as DevTab, label: "Premium",               icon: "ri-vip-crown-line" },
            { id: "colors"    as DevTab, label: "Renk Paleti",           icon: "ri-palette-line" },
            { id: "security"  as DevTab, label: "Güvenlik",              icon: "ri-shield-keyhole-line" },
          ]).map(t => (
            <button key={t.id} onClick={() => setDevTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                devTab === t.id
                  ? (t.id === "premium" ? "bg-amber-500/30 text-amber-300 border border-amber-500/40" : "bg-[#3B82F6] text-white")
                  : (t.id === "premium" ? "text-amber-400/70 hover:text-amber-300" : "text-[#7B8FAF] hover:text-white")
              }`}>
              <i className={`${t.icon} text-sm`}></i>
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">

        {/* ── Premium Özellikler ── */}
        {devTab === "premium" && popupEditorFeature && (
          <GenericPopupEditor
            featureId={popupEditorFeature}
            featureLabel={PREMIUM_FEATURES.find(f => f.id === popupEditorFeature)?.label ?? popupEditorFeature}
            onBack={() => setPopupEditorFeature(null)}
          />
        )}
        {devTab === "premium" && !popupEditorFeature && (
          <>
            <PremiumTrialBanner />

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
              <i className="ri-vip-crown-line text-amber-400 text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-amber-400/80 text-xs leading-relaxed">
                <strong>Premium Özellikler:</strong> Açıkken kontrol panelinde tam işlevselliğini kullanır.
                Kapalıyken <strong>Kilitle</strong> — panel menüsünde kilitli ikon görünür.
                <strong> Gizle</strong> — menüden tamamen kalkar.
              </p>
            </div>

            {/* Kategori filtresi */}
            <div>
              <p className="text-[#5E7398] text-xs uppercase tracking-widest mb-3">Kategoriler</p>
              <div className="flex flex-wrap gap-2">
                {premiumCategories.map(cat => (
                  <button key={cat} onClick={() => setActivePremiumCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap border ${
                      activePremiumCategory === cat
                        ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                        : "bg-[#0F1729] text-[#7B8FAF] border-[#2B3654] hover:border-amber-500/30"
                    }`}>
                    {cat}
                    {cat !== "Tümü" && (
                      <span className="ml-1.5 text-xs opacity-60">
                        ({PREMIUM_FEATURES.filter(f => f.category === cat).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredPremiumFeatures.map(feature => (
                <PremiumFeatureCard
                  key={feature.id}
                  feature={feature}
                  isOn={features[feature.id] ?? feature.defaultOn}
                  mode={modes[feature.id] ?? "hide"}
                  onToggle={handleToggle}
                  onModeChange={handleModeChange}
                  onEditPopup={PREMIUM_POPUP_CONFIGS[feature.id] ? setPopupEditorFeature : undefined}
                />
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button onClick={handleReset}
                className="flex-1 py-3 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-xl hover:text-white hover:border-[#3B82F6]/50 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                <i className="ri-refresh-line"></i> Varsayılanlara Sıfırla
              </button>
              <button onClick={handleSave}
                className={`flex-1 py-3 text-white text-sm rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 ${saved ? "bg-emerald-600" : "bg-amber-600 hover:bg-amber-700"}`}>
                {saved ? <><i className="ri-check-double-line"></i> Kaydedildi!</> : <><i className="ri-save-line"></i> Değişiklikleri Kaydet</>}
              </button>
            </div>
          </>
        )}

        {/* ── Renk Paleti ── */}
        {devTab === "colors" && <ColorPalettePanel />}



        {/* ── Güvenlik ── */}
        {devTab === "security" && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B82F6]/20 flex-shrink-0">
                <i className="ri-shield-keyhole-line text-[#3B82F6] text-xl"></i>
              </div>
              <div>
                <h2 className="text-white font-semibold">Şifreler & Güvenlik</h2>
                <p className="text-[#7B8FAF] text-xs">Geliştirici şifresi, admin şifresi ve 2FA yönetimi</p>
              </div>
            </div>
            <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-6">
              <PasswordChangePanel />
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
              <i className="ri-information-line text-amber-400 text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-amber-400/80 text-xs leading-relaxed">
                <strong>Geliştirici şifresi</strong> bu panele giriş için kullanılır.
                <strong> Admin şifresi</strong> kontrol paneline (/kontrol-paneli) giriş için kullanılır.
                Her iki şifre de şifrelenmiş olarak local storage'da saklanır.
              </p>
            </div>
          </div>
        )}

        {/* ── Özellik Yönetimi ── */}
        {devTab === "features" && (
          <>
            {/* Premium Deneme Baneri */}
            <PremiumTrialBanner />

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
              <i className="ri-information-line text-amber-400 text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-amber-400/80 text-xs leading-relaxed">
                <strong>Kilitle:</strong> özellik kilitli ikonu ile görünür, tıklanamaz. <strong>Gizle:</strong> menüden tamamen kalkar. Kaydedilmeden değişiklikler geçerli olmaz.
              </p>
            </div>

            {/* Kategori filtresi */}
            <div>
              <p className="text-[#5E7398] text-xs uppercase tracking-widest mb-3">Kategoriler</p>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button key={cat} onClick={() => setActiveCategory(cat)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap border ${
                      activeCategory === cat
                        ? "bg-[#3B82F6] text-white border-[#3B82F6]"
                        : "bg-[#0F1729] text-[#7B8FAF] border-[#2B3654] hover:border-[#3B82F6]/40"
                    }`}>
                    {cat}
                    {cat !== "Tümü" && (
                      <span className="ml-1.5 text-xs opacity-60">
                        ({FEATURES.filter(f => f.category === cat).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Kategorize grid */}
            {activeCategory === "Tümü" ? (
              <>
                {Array.from(new Set(FEATURES.map(f => f.category))).map(cat => (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[#7B8FAF] text-xs uppercase tracking-widest font-semibold">{cat}</span>
                      <div className="flex-1 h-px bg-[#2B3654]"></div>
                      <span className="text-[#465886] text-xs">{FEATURES.filter(f => f.category === cat && (features[f.id] ?? f.defaultOn)).length}/{FEATURES.filter(f => f.category === cat).length} aktif</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {FEATURES.filter(f => f.category === cat).map(feature => (
                        <FeatureCard key={feature.id} feature={feature} isOn={features[feature.id] ?? feature.defaultOn} mode={modes[feature.id] ?? "hide"}
                          onToggle={handleToggle} onModeChange={handleModeChange} />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredFeatures.map(feature => (
                  <FeatureCard key={feature.id} feature={feature} isOn={features[feature.id] ?? feature.defaultOn} mode={modes[feature.id] ?? "hide"}
                    onToggle={handleToggle} onModeChange={handleModeChange} />
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button onClick={handleReset}
                className="flex-1 py-3 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-xl hover:text-white hover:border-[#3B82F6]/50 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
                <i className="ri-refresh-line"></i> Varsayılanlara Sıfırla
              </button>
              <button onClick={handleSave}
                className={`flex-1 py-3 text-white text-sm rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 ${saved ? "bg-emerald-600" : "bg-[#3B82F6] hover:bg-[#3d4e6a]"}`}>
                {saved ? <><i className="ri-check-double-line"></i> Kaydedildi!</> : <><i className="ri-save-line"></i> Değişiklikleri Kaydet</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Generic Popup Editor ──────────────────────────────────────────────────────

const ICON_OPTIONS = [
  "ri-cursor-line", "ri-pencil-fill", "ri-building-2-line", "ri-arrow-go-back-line",
  "ri-links-line", "ri-layout-grid-line", "ri-history-line", "ri-palette-line",
  "ri-bar-chart-line", "ri-image-line", "ri-settings-3-line", "ri-keyboard-line",
  "ri-pages-line", "ri-time-line", "ri-check-double-line", "ri-star-line",
  "ri-shield-star-line", "ri-magic-line", "ri-rocket-line", "ri-eye-line",
];

const COLOR_OPTIONS = [
  "#10B981", "#60A5FA", "#F59E0B", "#A78BFA", "#F43F5E", "#34D399",
  "#FB923C", "#38BDF8", "#E879F9", "#FBBF24", "#6EE7B7", "#94A3B8",
];

interface GenericPopupEditorProps {
  featureId: string;
  featureLabel: string;
  onBack: () => void;
}

function GenericPopupEditor({ featureId, featureLabel, onBack }: GenericPopupEditorProps) {
  const baseConfig = PREMIUM_POPUP_CONFIGS[featureId];
  const [config, setConfig] = useState<PremiumPopupConfig>(() => getEditablePopupConfig(featureId));
  const [saved, setSaved] = useState(false);
  const [editingAdvIdx, setEditingAdvIdx] = useState<number | null>(null);

  const handleSave = () => {
    saveEditablePopupConfig(featureId, config);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    setConfig({ ...baseConfig });
    setSaved(false);
  };

  const upd = <K extends keyof PremiumPopupConfig>(key: K, val: PremiumPopupConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const updAdv = (idx: number, field: keyof PremiumPopupAdvantage, val: string) => {
    setConfig(prev => {
      const adv = [...prev.advantages];
      adv[idx] = { ...adv[idx], [field]: val };
      return { ...prev, advantages: adv };
    });
  };

  const addAdv = () => {
    const newAdv: PremiumPopupAdvantage = { icon: "ri-star-line", color: "#10B981", title: "Yeni Avantaj", description: "Avantaj açıklaması" };
    setConfig(prev => ({ ...prev, advantages: [...prev.advantages, newAdv] }));
  };

  const removeAdv = (idx: number) => {
    setConfig(prev => ({ ...prev, advantages: prev.advantages.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-[#7B8FAF] hover:text-white text-sm cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i> Geri
          </button>
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/15 flex-shrink-0">
            <i className={`${baseConfig?.icon || 'ri-star-line'} text-amber-400 text-xl`}></i>
          </div>
          <div>
            <h2 className="text-white font-semibold">{featureLabel} — Popup Düzenleme</h2>
            <p className="text-[#7B8FAF] text-xs">Kilitli özellik hover popup içeriğini özelleştirin</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2B3654] text-[#7B8FAF] text-xs rounded-lg hover:text-white cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-refresh-line"></i> Sıfırla
          </button>
          <button onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all ${saved ? "bg-emerald-600 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
            {saved ? <><i className="ri-check-double-line"></i> Kaydedildi!</> : <><i className="ri-save-line"></i> Kaydet</>}
          </button>
        </div>
      </div>

      {/* Hero image */}
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl overflow-hidden">
        <div className="relative h-36 overflow-hidden">
          <img src={config.heroImageUrl} alt="Hero" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1729]/80 to-transparent"></div>
          <div className="absolute bottom-2 left-3">
            <p className="text-white font-bold text-sm">{config.title}</p>
            <p className="text-[#A5B5CC] text-xs">{config.subtitle}</p>
          </div>
        </div>
        <div className="p-3">
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Hero Görsel URL</label>
          <input type="text" value={config.heroImageUrl} onChange={e => upd("heroImageUrl", e.target.value)}
            placeholder="https://..." className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-emerald-500 transition-colors" />
        </div>
      </div>

      {/* Ana metin alanları */}
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-5 space-y-4">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest font-semibold">Genel İçerik</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: "Badge Metni", key: "badgeText" as keyof PremiumPopupConfig, placeholder: "Premium Özellik" },
            { label: "Başlık",      key: "title"     as keyof PremiumPopupConfig, placeholder: "Özellik Adı" },
            { label: "Alt Başlık",  key: "subtitle"  as keyof PremiumPopupConfig, placeholder: "Kısa açıklama" },
            { label: "CTA Metni",   key: "ctaText"   as keyof PremiumPopupConfig, placeholder: "Aktifleştirmek için..." },
          ].map(field => (
            <div key={field.key}>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{field.label}</label>
              <input type="text" value={String(config[field.key])}
                onChange={e => upd(field.key, e.target.value as never)}
                placeholder={field.placeholder}
                className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors" />
            </div>
          ))}
        </div>
        <div>
          <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Açıklama</label>
          <textarea value={config.description} onChange={e => upd("description", e.target.value)} rows={3}
            className="w-full bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-emerald-500 transition-colors resize-none" />
        </div>
      </div>

      {/* İstatistikler */}
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-5">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest font-semibold mb-4">İstatistik Kutuları</p>
        <div className="grid grid-cols-3 gap-4">
          {config.stats.map((s, i) => (
            <div key={i} className="space-y-2">
              <div className="w-full h-1 rounded-full" style={{ background: s.color + "50" }}></div>
              <div>
                <label className="block text-[#7B8FAF] text-xs mb-1">Değer</label>
                <input type="text" value={s.value}
                  onChange={e => {
                    const newStats = [...config.stats];
                    newStats[i] = { ...newStats[i], value: e.target.value };
                    upd("stats", newStats);
                  }}
                  className="w-full bg-[#141E35] border border-[#2B3654] text-center font-bold rounded-lg px-2 py-2 text-sm outline-none focus:border-emerald-500"
                  style={{ color: s.color }} />
              </div>
              <div>
                <label className="block text-[#7B8FAF] text-xs mb-1">Etiket</label>
                <input type="text" value={s.label}
                  onChange={e => {
                    const newStats = [...config.stats];
                    newStats[i] = { ...newStats[i], label: e.target.value };
                    upd("stats", newStats);
                  }}
                  className="w-full bg-[#141E35] border border-[#2B3654] text-[#7B8FAF] text-center text-xs rounded-lg px-2 py-2 outline-none focus:border-emerald-500" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Avantaj Kartları */}
      <div className="bg-[#0F1729] border border-[#2B3654] rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-[#7B8FAF] text-xs uppercase tracking-widest font-semibold">Avantaj Kartları ({config.advantages.length})</p>
          <button onClick={addAdv}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-xs rounded-lg hover:bg-emerald-600/30 cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-add-line"></i> Yeni Kart
          </button>
        </div>
        <div className="space-y-2">
          {config.advantages.map((adv, idx) => (
            <div key={idx} className="bg-[#141E35] border border-[#2B3654] rounded-xl overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setEditingAdvIdx(editingAdvIdx === idx ? null : idx)}>
                <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: adv.color + "25" }}>
                  <i className={`${adv.icon} text-sm`} style={{ color: adv.color }}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{adv.title}</p>
                  <p className="text-[#5E7398] text-xs truncate">{adv.description}</p>
                </div>
                <button onClick={e => { e.stopPropagation(); removeAdv(idx); }}
                  className="w-6 h-6 flex items-center justify-center rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 cursor-pointer">
                  <i className="ri-delete-bin-line text-xs"></i>
                </button>
                <i className={`${editingAdvIdx === idx ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#3B82F6] text-sm`}></i>
              </div>
              {editingAdvIdx === idx && (
                <div className="px-4 pb-4 border-t border-[#2B3654] pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#7B8FAF] text-xs mb-1.5">Başlık</label>
                      <input type="text" value={adv.title} onChange={e => updAdv(idx, "title", e.target.value)}
                        className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="block text-[#7B8FAF] text-xs mb-1.5">Açıklama</label>
                      <input type="text" value={adv.description} onChange={e => updAdv(idx, "description", e.target.value)}
                        className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-emerald-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#7B8FAF] text-xs mb-2">İkon</label>
                    <div className="flex flex-wrap gap-1.5">
                      {ICON_OPTIONS.map(ic => (
                        <button key={ic} onClick={() => updAdv(idx, "icon", ic)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg border cursor-pointer transition-colors ${adv.icon === ic ? "border-emerald-500/60 bg-emerald-500/20" : "border-[#2B3654] bg-[#0F1729] hover:border-[#3B82F6]/50"}`}
                          title={ic}>
                          <i className={`${ic} text-sm`} style={{ color: adv.icon === ic ? adv.color : "#7B8FAF" }}></i>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#7B8FAF] text-xs mb-2">Renk</label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_OPTIONS.map(c => (
                        <button key={c} onClick={() => updAdv(idx, "color", c)}
                          className={`w-7 h-7 rounded-full border-2 cursor-pointer transition-all ${adv.color === c ? "border-white scale-110" : "border-transparent hover:border-white/40"}`}
                          style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="bg-[#0F1729] border border-emerald-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
        <i className="ri-information-line text-emerald-400 text-sm flex-shrink-0 mt-0.5"></i>
        <p className="text-[#7B8FAF] text-xs leading-relaxed">
          Değişiklikler kaydedildikten sonra kontrol panelinde o özelliğin butonu üzerine hover yaparak popup önizlemesini görebilirsiniz.
        </p>
      </div>

      <div className="flex gap-3 pb-4">
        <button onClick={handleReset}
          className="flex-1 py-3 border border-[#2B3654] text-[#7B8FAF] text-sm rounded-xl hover:text-white cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2">
          <i className="ri-refresh-line"></i> Varsayılana Dön
        </button>
        <button onClick={handleSave}
          className={`flex-1 py-3 text-white text-sm rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2 ${saved ? "bg-emerald-600" : "bg-[#3B82F6] hover:bg-[#3d4e6a]"}`}>
          {saved ? <><i className="ri-check-double-line"></i> Kaydedildi!</> : <><i className="ri-save-line"></i> Değişiklikleri Kaydet</>}
        </button>
      </div>
    </div>
  );
}

// ── Premium Feature Card Component ──────────────────────────────────────────

function PremiumFeatureCard({
  feature, isOn, mode, onToggle, onModeChange, onEditPopup
}: {
  feature: FeatureToggle; isOn: boolean; mode: FeatureMode;
  onToggle: (id: string) => void; onModeChange: (id: string, m: FeatureMode) => void;
  onEditPopup?: (featureId: string) => void;
}) {
  const cfg = PREMIUM_POPUP_CONFIGS[feature.id];

  return (
    <div className={`bg-[#0F1729] border rounded-xl overflow-hidden transition-all ${
      isOn ? "border-amber-500/30" : "border-[#2B3654]/50 opacity-75"
    }`}>
      {/* Hero thumbnail */}
      {cfg?.heroImageUrl && (
        <div className="relative h-24 overflow-hidden">
          <img src={cfg.heroImageUrl} alt={feature.label} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1729] via-[#0F1729]/40 to-transparent"></div>
          <div className="absolute top-2 left-3">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
              isOn ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-[#2B3654]/60 text-[#7B8FAF] border-[#2B3654]"
            }`}>
              <i className="ri-vip-crown-fill text-[9px]"></i> Premium
            </span>
          </div>
          <div className="absolute top-2 right-3">
            <button onClick={() => onToggle(feature.id)}
              className={`relative w-9 h-5 rounded-full transition-all cursor-pointer ${
                isOn ? "bg-amber-500" : "bg-[#2B3654]"
              }`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm ${
                isOn ? "left-4" : "left-0.5"
              }`}></span>
            </button>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${
            isOn ? "bg-amber-500/20" : "bg-white/5"
          }`}>
            <i className={`${feature.icon} text-base ${
              isOn ? "text-amber-400" : "text-[#465886]"
            }`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${isOn ? "text-white" : "text-[#7B8FAF]"}`}>{feature.label}</p>
            <p className="text-[#5E7398] text-xs mt-0.5 leading-relaxed line-clamp-2">{feature.description}</p>
          </div>
        </div>

        {/* Stats */}
        {cfg?.stats && isOn && (
          <div className="flex gap-2 mt-3">
            {cfg.stats.map((s, i) => (
              <div key={i} className="flex-1 text-center bg-[#141E35] rounded-lg py-1.5">
                <p className="text-xs font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-[#465886] text-[9px] leading-tight mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {!isOn && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-[#465886] text-xs whitespace-nowrap">Kapalıyken:</span>
            <div className="flex gap-1.5">
              <button onClick={() => onModeChange(feature.id, "lock")}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  mode === "lock" ? "bg-amber-500/20 text-amber-400 border-amber-500/40" : "bg-transparent text-[#5E7398] border-[#2B3654] hover:border-amber-500/30"
                }`}>
                <i className="ri-lock-line text-xs"></i> Kilitle
              </button>
              <button onClick={() => onModeChange(feature.id, "hide")}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  mode === "hide" ? "bg-red-500/20 text-red-400 border-red-500/40" : "bg-transparent text-[#5E7398] border-[#2B3654] hover:border-red-500/30"
                }`}>
                <i className="ri-eye-off-line text-xs"></i> Gizle
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-2">
          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
            isOn ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
              : mode === "lock" ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
              : "text-red-400 bg-red-500/10 border-red-500/20"
          }`}>
            {isOn ? <><i className="ri-vip-crown-fill"></i> Aktif</> : mode === "lock" ? <><i className="ri-lock-line"></i> Kilitli</> : <><i className="ri-eye-off-line"></i> Gizli</>}
          </span>
          {onEditPopup && (
            <button
              onClick={() => onEditPopup(feature.id)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs cursor-pointer whitespace-nowrap border border-[#2B3654] text-[#7B8FAF] hover:text-white hover:border-[#3B82F6]/50 transition-colors bg-[#0F1729]"
            >
              <i className="ri-edit-2-line text-xs"></i> Popup Düzenle
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Feature Card Component ────────────────────────────────────────────────────

function FeatureCard({
  feature, isOn, mode,
  onToggle, onModeChange,
}: {
  feature: FeatureToggle;
  isOn: boolean;
  mode: FeatureMode;
  onToggle: (id: string) => void;
  onModeChange: (id: string, m: FeatureMode) => void;
}) {
  return (
    <div className={`bg-[#0F1729] border rounded-xl p-4 transition-all ${isOn ? "border-[#2B3654]" : "border-[#2B3654]/50 opacity-75"}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${isOn ? "bg-[#3B82F6]/20" : "bg-white/5"}`}>
          <i className={`${feature.icon} text-lg ${isOn ? "text-[#3B82F6]" : "text-[#465886]"}`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className={`text-sm font-semibold ${isOn ? "text-white" : "text-[#7B8FAF]"}`}>{feature.label}</p>
                {/* Premium Aktif Rozeti — özellik açıkken görünür */}
                {isOn && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[10px] font-semibold whitespace-nowrap">
                    <i className="ri-vip-crown-fill text-[9px]"></i>
                    Premium Aktif
                  </span>
                )}
              </div>
              <span className="text-[#465886] text-xs">{feature.category}</span>
            </div>
            <button onClick={() => onToggle(feature.id)}
              className={`relative w-10 h-5 rounded-full transition-all cursor-pointer flex-shrink-0 mt-0.5 ${isOn ? "bg-[#3B82F6]" : "bg-[#2B3654]"}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow-sm ${isOn ? "left-5" : "left-0.5"}`}></span>
            </button>
          </div>
          <p className="text-[#5E7398] text-xs mt-1 leading-relaxed">{feature.description}</p>

          {!isOn && (
            <div className="mt-2.5 flex items-center gap-2">
              <span className="text-[#465886] text-xs whitespace-nowrap">Kapalıyken:</span>
              <div className="flex gap-1.5">
                <button onClick={() => onModeChange(feature.id, "lock")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    mode === "lock" ? "bg-amber-500/20 text-amber-400 border-amber-500/40" : "bg-transparent text-[#5E7398] border-[#2B3654] hover:border-amber-500/30 hover:text-amber-400"
                  }`}>
                  <i className="ri-lock-line text-xs"></i> Kilitle
                </button>
                <button onClick={() => onModeChange(feature.id, "hide")}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                    mode === "hide" ? "bg-red-500/20 text-red-400 border-red-500/40" : "bg-transparent text-[#5E7398] border-[#2B3654] hover:border-red-500/30 hover:text-red-400"
                  }`}>
                  <i className="ri-eye-off-line text-xs"></i> Gizle
                </button>
              </div>
            </div>
          )}

          <span className={`mt-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
            isOn ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
              : mode === "lock" ? "text-amber-400 bg-amber-500/10 border-amber-500/20"
              : "text-red-400 bg-red-500/10 border-red-500/20"
          }`}>
            {isOn ? "Aktif" : mode === "lock" ? <><i className="ri-lock-line"></i> Kilitli</> : <><i className="ri-eye-off-line"></i> Gizli</>}
          </span>
        </div>
      </div>
    </div>
  );
}
